import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Slider, styled} from '@mui/material';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import {IRREGULAR_ICON_SIZE} from '../constants';

const MOUSE_ENTER_DELAY_MS = 150;
const MOUSE_LEAVE_DELAY_MS = 200;

// Volume Control Component
interface VolumeControlProps {
  isMuted: boolean;
  onToggleMute: (event: React.MouseEvent<HTMLDivElement>) => void;
  videoVolume?: number;
  onVolumeChange?: (volume: number) => void;
  allowVolumeControl: boolean;
  theme?: 'dark' | 'light';
  color?: string;
}

interface ColorScheme {
  buttonColor: string;
  controlBackgroundColor: string;
  trackColor: string;
  railColor: string;
  thumbColor: string;
}

const getColorScheme = () => ({
  dark: {
    buttonColor: designSystemToken(`semantic.bg.secondary`),
    controlBackgroundColor: designSystemToken('semantic.bg.alpha'),
    trackColor: designSystemToken(`semantic.bg.secondary`),
    railColor: designSystemToken('semantic.fg.inverse-secondary'),
    thumbColor: designSystemToken(`semantic.bg.secondary`),
  },
  light: {
    buttonColor: designSystemToken('semantic.fg.primary'),
    controlBackgroundColor: designSystemToken('semantic.bg.alpha'),
    trackColor: designSystemToken('semantic.fg.primary'),
    railColor: designSystemToken('semantic.fg.secondary'),
    thumbColor: designSystemToken('semantic.fg.primary'),
  },
});

const shouldNotForwardProps = ['colorScheme'];

// Volume Control Styles
const VolumeSlider = styled(Slider, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({colorScheme}: {colorScheme: ColorScheme}) => ({
  'width': '4px',
  '& .MuiSlider-track': {
    backgroundColor: colorScheme.trackColor,
    border: 'unset',
  },
  '& .MuiSlider-rail': {
    backgroundColor: colorScheme.railColor,
  },
  '& .MuiSlider-thumb': {
    'backgroundColor': colorScheme.thumbColor,
    'width': '16px',
    'height': '16px',
    'boxShadow': 'none',
    '&:hover, &.Mui-focusVisible': {
      width: '18px',
      height: '18px',
    },
  },
}));

function VolumeControl({
  isMuted,
  onToggleMute,
  videoVolume = 1.0,
  onVolumeChange,
  allowVolumeControl,
  theme = 'light',
  color,
}: VolumeControlProps) {
  const [localVolume, setLocalVolume] = useState(videoVolume);
  const [open, setOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const colorScheme = getColorScheme()[theme];
  const iconColor = color ?? colorScheme.buttonColor;

  const handleVolumeChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      const volume = Array.isArray(newValue) ? newValue[0] : newValue;
      setLocalVolume(volume);
      onVolumeChange?.(volume);
    },
    [onVolumeChange]
  );

  const handleMouseEnter = useCallback(() => {
    if (!allowVolumeControl) {
      return;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setOpen(true);
    }, MOUSE_ENTER_DELAY_MS);
  }, [allowVolumeControl]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, MOUSE_LEAVE_DELAY_MS);
  }, []);

  const handleSliderMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setOpen(true);
  }, []);

  // Sync local volume with videoVolume prop
  useEffect(() => {
    setLocalVolume(videoVolume);
  }, [videoVolume]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      style={{
        marginLeft: '8px',
        position: 'relative',
        display: 'inline-block',
      }}
    >
      <Box
        onClick={onToggleMute}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: 'pointer',
        }}
      >
        <Icon
          name={isMuted ? 'Features-Audio-Mute' : 'Features-Audio-On'}
          size="medium"
          appearance="neutral"
          color={iconColor}
          style={{
            width: `${IRREGULAR_ICON_SIZE}px`,
            height: `${IRREGULAR_ICON_SIZE}px`,
          }}
        />
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: colorScheme.controlBackgroundColor,
            borderRadius: '8px',
            padding: '12px 4px',
            zIndex: 10000,
          }}
          onMouseEnter={handleSliderMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <VolumeSlider
            value={isMuted ? 0 : localVolume}
            onChange={handleVolumeChange}
            onClick={(e) => e.stopPropagation()}
            min={0}
            max={1}
            step={0.1}
            orientation="vertical"
            sx={{height: '60px'}}
            colorScheme={colorScheme}
          />
        </Box>
      )}
    </Box>
  );
}

export default VolumeControl;
